import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import type { TimeType } from './../enums/TimeType';
import { formatDateIso } from '@/utilities/dateutils';

export class ContestantAndRaceContestantTime extends BaseObj {
    public ID: number;
    public __id: number;

    public AccumulatedTime: number | null;
    public __accumulatedtime: number | null;

    public Bib: string | null;
    public __bib: string | null;

    public ContestantID: number | null;
    public __contestantid: number | null;

    public Description: string | null;
    public __description: string | null;

    public Disabled: boolean;
    public __disabled: boolean;

    public LapNumber: number | null;
    public __lapnumber: number | null;

    public PenaltyString: string | null;
    public __penaltystring: string | null;

    public PointsMs: number | null;
    public __pointsms: number | null;

    public RaceContestantID: number | null;
    public __racecontestantid: number | null;

    public RaceID: number;
    public __raceid: number;

    public StationID: number | null;
    public __stationid: number | null;

    public StationRoleID: number | null;
    public __stationroleid: number | null;

    public Time: Date;
    public __time: Date;

    public TimeReason: string | null;
    public __timereason: string | null;

    public TimeType: TimeType;
    public __timetype: TimeType;

    public TUID: string | null;
    public __tuid: string | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        AccumulatedTime?: number | null;
        Bib?: string | null;
        ContestantID?: number | null;
        Description?: string | null;
        Disabled?: boolean;
        EntityState?: EntityState;
        LapNumber?: number | null;
        PenaltyString?: string | null;
        PointsMs?: number | null;
        RaceContestantID?: number | null;
        RaceID?: number;
        StationID?: number | null;
        StationRoleID?: number | null;
        Time?: Date;
        TimeReason?: string | null;
        TimeType?: TimeType;
        TUID?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<ContestantAndRaceContestantTime>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn(
                    'ContestantAndRaceContestantTime tried to set entitystate, but entitystate is not a ref! This should never happen',
                );
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.AccumulatedTime = values.AccumulatedTime ?? null;
        this.__accumulatedtime = values.AccumulatedTime ?? null;
        Object.defineProperty(this, 'AccumulatedTime', {
            get(): number | null {
                return this.__accumulatedtime;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('AccumulatedTime', this.__accumulatedtime, value);
                this.__accumulatedtime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Bib = values.Bib ?? null;
        this.__bib = values.Bib ?? null;
        Object.defineProperty(this, 'Bib', {
            get(): string | null {
                return this.__bib;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Bib', this.__bib, value);
                this.__bib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantID = values.ContestantID ?? null;
        this.__contestantid = values.ContestantID ?? null;
        Object.defineProperty(this, 'ContestantID', {
            get(): number | null {
                return this.__contestantid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ContestantID', this.__contestantid, value);
                this.__contestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Description = values.Description ?? null;
        this.__description = values.Description ?? null;
        Object.defineProperty(this, 'Description', {
            get(): string | null {
                return this.__description;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Description', this.__description, value);
                this.__description = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Disabled = values.Disabled ?? false;
        this.__disabled = values.Disabled ?? false;
        Object.defineProperty(this, 'Disabled', {
            get(): boolean {
                return this.__disabled;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Disabled', this.__disabled, value);
                this.__disabled = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LapNumber = values.LapNumber ?? null;
        this.__lapnumber = values.LapNumber ?? null;
        Object.defineProperty(this, 'LapNumber', {
            get(): number | null {
                return this.__lapnumber;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('LapNumber', this.__lapnumber, value);
                this.__lapnumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PenaltyString = values.PenaltyString ?? null;
        this.__penaltystring = values.PenaltyString ?? null;
        Object.defineProperty(this, 'PenaltyString', {
            get(): string | null {
                return this.__penaltystring;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('PenaltyString', this.__penaltystring, value);
                this.__penaltystring = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PointsMs = values.PointsMs ?? null;
        this.__pointsms = values.PointsMs ?? null;
        Object.defineProperty(this, 'PointsMs', {
            get(): number | null {
                return this.__pointsms;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('PointsMs', this.__pointsms, value);
                this.__pointsms = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestantID = values.RaceContestantID ?? null;
        this.__racecontestantid = values.RaceContestantID ?? null;
        Object.defineProperty(this, 'RaceContestantID', {
            get(): number | null {
                return this.__racecontestantid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('RaceContestantID', this.__racecontestantid, value);
                this.__racecontestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceID = values.RaceID ?? 0;
        this.__raceid = values.RaceID ?? 0;
        Object.defineProperty(this, 'RaceID', {
            get(): number {
                return this.__raceid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceID', this.__raceid, value);
                this.__raceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationID = values.StationID ?? null;
        this.__stationid = values.StationID ?? null;
        Object.defineProperty(this, 'StationID', {
            get(): number | null {
                return this.__stationid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('StationID', this.__stationid, value);
                this.__stationid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationRoleID = values.StationRoleID ?? null;
        this.__stationroleid = values.StationRoleID ?? null;
        Object.defineProperty(this, 'StationRoleID', {
            get(): number | null {
                return this.__stationroleid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('StationRoleID', this.__stationroleid, value);
                this.__stationroleid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Time = values.Time ?? new Date('1970-01-01T00:00:00.000');
        this.__time = values.Time ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'Time', {
            get(): Date {
                return this.__time;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('Time', this.__time, value);
                this.__time = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TimeReason = values.TimeReason ?? null;
        this.__timereason = values.TimeReason ?? null;
        Object.defineProperty(this, 'TimeReason', {
            get(): string | null {
                return this.__timereason;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('TimeReason', this.__timereason, value);
                this.__timereason = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TimeType = values.TimeType ?? 'None';
        this.__timetype = values.TimeType ?? 'None';
        Object.defineProperty(this, 'TimeType', {
            get(): TimeType {
                return this.__timetype;
            },
            set(value: TimeType) {
                this.__entityStateHandler.propertyChanged('TimeType', this.__timetype, value);
                this.__timetype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TUID = values.TUID ?? null;
        this.__tuid = values.TUID ?? null;
        Object.defineProperty(this, 'TUID', {
            get(): string | null {
                return this.__tuid;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('TUID', this.__tuid, value);
                this.__tuid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"AccumulatedTime": ${JSON.stringify(this.AccumulatedTime)}` +
            ',' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"ContestantID": ${JSON.stringify(this.ContestantID)}` +
            ',' +
            `"Description": ${JSON.stringify(this.Description)}` +
            ',' +
            `"Disabled": ${JSON.stringify(this.Disabled)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"LapNumber": ${JSON.stringify(this.LapNumber)}` +
            ',' +
            `"PenaltyString": ${JSON.stringify(this.PenaltyString)}` +
            ',' +
            `"PointsMs": ${JSON.stringify(this.PointsMs)}` +
            ',' +
            `"RaceContestantID": ${JSON.stringify(this.RaceContestantID)}` +
            ',' +
            `"RaceID": ${JSON.stringify(this.RaceID)}` +
            ',' +
            `"StationID": ${JSON.stringify(this.StationID)}` +
            ',' +
            `"StationRoleID": ${JSON.stringify(this.StationRoleID)}` +
            ',' +
            `"Time": ${'"' + formatDateIso(this.Time) + '"'}` +
            ',' +
            `"TimeReason": ${JSON.stringify(this.TimeReason)}` +
            ',' +
            `"TimeType": ${this.TimeType == 'None' ? 0 : this.TimeType == 'RaceContestantTime' ? 1 : this.TimeType == 'ContestantTime' ? 2 : this.TimeType == 'DNF' ? 3 : this.TimeType == 'DNS' ? 4 : this.TimeType == 'DSQ' ? 5 : this.TimeType == 'DNQ' ? 6 : this.TimeType == 'LAP' ? 7 : this.TimeType == 'Finished' ? 8 : this.TimeType == 'DNC' ? 9 : this.TimeType == 'REL' ? 10 : this.TimeType == 'EXC' ? 11 : 0}` +
            ',' +
            `"TUID": ${JSON.stringify(this.TUID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of ContestantAndRaceContestantTime from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        AccumulatedTime?: number | null;
        Bib?: string | null;
        ContestantID?: number | null;
        Description?: string | null;
        Disabled?: boolean;
        LapNumber?: number | null;
        PenaltyString?: string | null;
        PointsMs?: number | null;
        RaceContestantID?: number | null;
        RaceID?: number;
        StationID?: number | null;
        StationRoleID?: number | null;
        Time?: Date;
        TimeReason?: string | null;
        TimeType?: TimeType;
        TUID?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): ContestantAndRaceContestantTime {
        return new ContestantAndRaceContestantTime(values);
    }

    public static _FromServerJson(json: any): ContestantAndRaceContestantTime {
        return new ContestantAndRaceContestantTime({
            ID: json.ID,
            AccumulatedTime: json.AccumulatedTime ?? null,
            Bib: json.Bib ?? '',
            ContestantID: json.ContestantID ?? null,
            Description: json.Description ?? '',
            Disabled: json.Disabled,
            LapNumber: json.LapNumber ?? null,
            PenaltyString: json.PenaltyString ?? '',
            PointsMs: json.PointsMs ?? null,
            RaceContestantID: json.RaceContestantID ?? null,
            RaceID: json.RaceID,
            StationID: json.StationID ?? null,
            StationRoleID: json.StationRoleID ?? null,
            Time: json.Time instanceof Date ? json.Time : new Date(json.Time),
            TimeReason: json.TimeReason ?? '',
            TimeType:
                json.TimeType == 0
                    ? 'None'
                    : json.TimeType == 1
                      ? 'RaceContestantTime'
                      : json.TimeType == 2
                        ? 'ContestantTime'
                        : json.TimeType == 3
                          ? 'DNF'
                          : json.TimeType == 4
                            ? 'DNS'
                            : json.TimeType == 5
                              ? 'DSQ'
                              : json.TimeType == 6
                                ? 'DNQ'
                                : json.TimeType == 7
                                  ? 'LAP'
                                  : json.TimeType == 8
                                    ? 'Finished'
                                    : json.TimeType == 9
                                      ? 'DNC'
                                      : json.TimeType == 10
                                        ? 'REL'
                                        : json.TimeType == 11
                                          ? 'EXC'
                                          : 'None',
            TUID: json.TUID ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of ContestantAndRaceContestantTime from another instance of ContestantAndRaceContestantTime.
     * @param other The ContestantAndRaceContestantTime you want to clone
     * @returns A cloned ContestantAndRaceContestantTime
     */
    public static FromOther(other: ContestantAndRaceContestantTime): ContestantAndRaceContestantTime {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return ContestantAndRaceContestantTime._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ContestantAndRaceContestantTime.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'AccumulatedTime'
            | 'Bib'
            | 'ContestantID'
            | 'Description'
            | 'Disabled'
            | 'LapNumber'
            | 'PenaltyString'
            | 'PointsMs'
            | 'RaceContestantID'
            | 'RaceID'
            | 'StationID'
            | 'StationRoleID'
            | 'Time'
            | 'TimeReason'
            | 'TimeType'
            | 'TUID'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'ContestantAndRaceContestantTime';
}
